// utils
import apiCall from "../utils/api";
import {addOneDay, getKey, getTimestamp} from "../utils/common"; // formatDate
// store
import {mapActions, mapGetters} from "vuex";
import {AUTH_LOGOUT} from "../store/actions/auth";
import {
    ADD_MULTI_DATES,
    GET_ALT_DATE_RANGE,
    GET_END_CHART_DATE,
    GET_HAS_MULTI_DATES,
    GET_INIT_QUERY_DONE,
    GET_MULTI_DATES,
    GET_MULTI_DATES_ONLY,
    GET_START_CHART_DATE,
    GET_SUBSCRIBE_KEY,
    MODIFY_MULTI_DATE,
    RESTORE_MULTI_DATE,
    SET_INIT_QUERY_DONE
} from "../store/actions/sensors";
import {
    hideZoomIconFor,
    levelMeters,
    NO_CURRENT_VALUE_CHARTS,
    precipitationSensors,
    sensorsTableMap
} from "../constants/sensors-constants";

export default {
        async mounted() {
            /**
             * generate 3 months time delta
             * change dateRange start to be 3 months in the past
             * if has multiDates (ex. Inclinometers requests), add last 3 months in multiDates
             * change dateRange.startModified, dateRange.endModified to true
             * get data
             */
            await this.prepareDataAndFetch();
        },
        computed: {
            ...mapGetters([
                'currentDamSelectedId',
                'getActiveStation',
                GET_SUBSCRIBE_KEY,
                GET_START_CHART_DATE,
                GET_END_CHART_DATE,
                GET_HAS_MULTI_DATES,
                GET_MULTI_DATES,
                GET_MULTI_DATES_ONLY,
                GET_ALT_DATE_RANGE,
                GET_INIT_QUERY_DONE
            ]),
            metrics() {
                if (this.isPrecipitationType) {
                    if (this.precipitationTypes > 1) {
                        return 'units.liters-combined';
                    } else {
                        return 'units.liters-24h';
                    }
                }
                return this.metric;
            },
            isZoomShown() {
                return !(this.psObjectName && hideZoomIconFor.includes(this.psObjectName));
            },
            isLevelmeterType() {
                return (this.psObjectName && levelMeters.includes(this.psObjectName));
            },
            isPrecipitationType() {
                return (this.psObjectName && precipitationSensors.includes(this.psObjectName));
            },
            notInIgnoreList() {
                return (this.psObjectName && !NO_CURRENT_VALUE_CHARTS.includes(this.psObjectName));
            },
            highestWorkingWaterLevel() {
                const allStationData = this.getActiveStation;
                let highestWorkingWaterLevel = '-';  // N/A text is too long in BG

                try {
                    if (allStationData) {
                        const levelData = allStationData[7]['highest_working_water_level'];
                        if (levelData) {
                            highestWorkingWaterLevel = levelData;
                        }
                    }
                }
                catch (e) {
                    // pass
                }
                return highestWorkingWaterLevel;
            },
            precipitationTypes() {
                if (this.chartData.series.length > 0) {
                    return this.chartData.series.length;
                }
                return 1;
            },
            dateStart() {
                if (this.$route.query?.tab && sensorsTableMap[this.$route.query?.tab]) {
                    return this.$store.state.sensors.dateRange?.[sensorsTableMap[this.$route.query?.tab]]?.start || this.GET_START_CHART_DATE;
                }
                return this.GET_START_CHART_DATE;
            },
            dateEnd() {
                if (this.$route.query?.tab && sensorsTableMap[this.$route.query?.tab]) {
                    const theEnd = this.$store.state.sensors.dateRange?.[sensorsTableMap[this.$route.query?.tab]]?.end;
                    if (theEnd) {
                        return theEnd;
                    }
                }
                return this.GET_END_CHART_DATE;
            },
            dateEndPlusOne() {
                return addOneDay(this.dateEnd);
            }
        },
        methods: {
            ...mapActions([
                AUTH_LOGOUT,
                ADD_MULTI_DATES,
                MODIFY_MULTI_DATE,
                RESTORE_MULTI_DATE,
                SET_INIT_QUERY_DONE
            ]),
            /**
             * Prepare the data and do fetch.
             * @returns {Promise<void>}
             */
            async prepareDataAndFetch() {
                // update this.multiDates array manually if
                // datetime picker OK clicked straight away
                if (this.hasMultiDates === true && this.GET_MULTI_DATES.length === 0) {
                    this.ADD_MULTI_DATES(this.GET_ALT_DATE_RANGE);
                }

                // if it has multi dates (inclinometer), use those
                let multiDatesToUse = [];
                if (this.hasMultiDates) {
                    multiDatesToUse = this.GET_MULTI_DATES_ONLY;
                }

                const data = {
                    "start_time": this.dateStart,
                    "end_time": this.dateEndPlusOne,
                    "date_range": multiDatesToUse,
                    "init_query": this.GET_INIT_QUERY_DONE,
                    "show_statistics": this.showStatistics,
                    "ps_obj_name": this.psObjectName,
                    "ps_name_index": this.currentDamSelectedId,
                    "alternative": this.alternative,
                    "show_plot_line": this.showPlotLine,
                    "show_triple_data": this.showTripleData,
                    "measuring_profile": this.measuringProfile
                };

                this.fetchChartData(data);
            },
            /**
             * Fetch data for given period and specified ps_obj_name (sensor).
             * Show statistics is for reverse compatibility.
             * Result should be like {x: [datetime,], y: [values,]}
             * @returns {Promise<void>}
             */
            async fetchChartData(data) {
                this.init = false;
                this.loading = true;
                this.error = false;
                try {

                    const response = await apiCall.getSensorChart(data);
                    const seriesRaw = response.data.result.chart.series;

                    // if no data for multi date, color pill grey
                    if (this.hasMultiDates) {
                        // reset init query
                        this.SET_INIT_QUERY_DONE();

                        // get dates from response
                        const responseDateKeys = seriesRaw.map((item) => {
                           return item.name;
                        });

                        // for each multi date, check if it is in the upper list, else set grey
                        for (let index = 0; index < this.GET_MULTI_DATES.length; index++) {
                            if (!responseDateKeys.includes(this.GET_MULTI_DATES[index].date)) {
                                this.MODIFY_MULTI_DATE(index);
                            }

                            else if (responseDateKeys.includes(this.GET_MULTI_DATES[index].date) &&
                                this.GET_MULTI_DATES[index].hasSeries === false
                            ) {
                                this.RESTORE_MULTI_DATE(index);
                            }
                        }
                        // do the opposite as to add any new dates to the pills
                        for (let index = 0; index < responseDateKeys.length; index++) {
                            if (!this.GET_MULTI_DATES_ONLY.includes(responseDateKeys[index])) {
                                this.ADD_MULTI_DATES(responseDateKeys[index]);
                            }
                        }
                    }

                    // get the data and beautify the name
                    const flossedSeriesData = this.beautifySeriesNames(seriesRaw);

                    // reset datasets by using the received value
                    this.chartData.series = flossedSeriesData;
                    // plot lines if any
                    this.chartData.plotLines = response.data.result.chart.plotLines;
                    // add start and end date as min and max for xAxis (convert to timestamp)
                    this.chartData.startDate = getTimestamp(this.dateStart);
                    this.chartData.endDate = getTimestamp(this.dateEnd);
                    // set the chart type, given by BE
                    this.typeChart = response.data.type;
                    // set the reverse (for reverse-spline)
                    this.reverse = response.data.reverse;

                    this.loading = false;
                }
                catch (error) {
                    this.loading = false;
                    this.error = true;
                    console.log(error);
                    if (error.request.status === 401) {
                        // if resp is unauthorized, logout
                        this.AUTH_LOGOUT();
                    }
                }
            },
            /**
             * This will force the prop to be changed.
             * In the chart component this prop is watched,
             * a resetZoom function will be called.
             */
            changeZoomKey() {
                this.zoomKey = getKey();
            },
            /**
             * Emit resize to parent and refresh key
             * so that the chart will re-render.
             */
            toggleContainerSize() {
                this.$emit('resize');
                this.resizeKey = getKey();
            },
            /**
             * Fixes measurement value and metric locale if needed.
             * @param measurement
             * @param metric
             * @returns {string}
             */
            combineMeasurementUnit(measurement, metric) {
                let fixedMeasurement = '0.00';
                let localizedMetric = metric;

                if (measurement) {
                    fixedMeasurement = measurement;
                }

                if (this.$i18n.te(metric)) {
                    localizedMetric = this.$i18n.t(metric);
                }

                if (localizedMetric) {
                    return `${fixedMeasurement} ${localizedMetric}`;
                }
                return `${fixedMeasurement}`;
            },
            getPrecipitationMeasurements(measurement) {
                const litersDayMessage = `${(measurement / 24).toFixed(2)} ${this.$i18n.t('sensors-page.liters-day')}`;
                if (this.precipitationTypes > 1) {
                    return `${litersDayMessage}, ${measurement.toFixed(2)} ${this.$i18n.t('sensors-page.liters-hour')}`;
                } else {
                    return litersDayMessage;
                }
            },
        },
        watch: {
            async GET_SUBSCRIBE_KEY() {
                await this.prepareDataAndFetch();
            }
        }
};